import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReportUserAvatar from '../components/ReportUserAvatar';
import ReportActionModal from '../components/ReportActionModal';
import ReportDetailsDrawer from '../components/ReportDetailsDrawer';
import { getReportedUsers } from '../services/reportService';
import {
  REPORT_SORTS,
  REPORT_TABS,
  emitPendingReportsCount,
  formatDate,
  formatTime,
  getAccountStatus,
  pluralize,
} from '../constants/reports';

const PAGE_SIZE = 20;
const TOAST_DURATION_MS = 4000;
const SKELETON_ROWS = 6;

const TAB_VALUES = REPORT_TABS.map((t) => t.value);
const SORT_VALUES = REPORT_SORTS.map((s) => s.value);

function getPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = [1];

  if (current > 3) items.push('…');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    items.push(pageNum);
  }

  if (current < total - 2) items.push('…');

  items.push(total);
  return items;
}

// Unknown or missing query values fall back to the API defaults.
function readParams(searchParams) {
  const status = searchParams.get('status');
  const sort = searchParams.get('sort');
  const page = parseInt(searchParams.get('page'), 10);
  return {
    status: TAB_VALUES.includes(status) ? status : 'pending',
    sort: SORT_VALUES.includes(sort) ? sort : 'most_reported',
    page: page > 0 ? page : 1,
    search: (searchParams.get('search') || '').trim(),
    openUserId: searchParams.get('user') || '',
  };
}

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { status, sort, page, search, openUserId } = readParams(searchParams);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchInput, setSearchInput] = useState(search);
  const [actionTarget, setActionTarget] = useState(null);
  const [drawerUpdate, setDrawerUpdate] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const scrollRef = useRef(null);
  const lastWrittenSearch = useRef(search);

  const updateParams = useCallback((changes, { resetPage = false } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === '' || value == null) next.delete(key);
        else next.set(key, String(value));
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const showToast = useCallback((text, kind, action) => {
    clearTimeout(toastTimer.current);
    setToast({ text, kind, action });
    toastTimer.current = setTimeout(() => setToast(null), action ? TOAST_DURATION_MS * 2 : TOAST_DURATION_MS);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Debounce the search box into the URL so we don't hit the API on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      const value = searchInput.trim();
      if (value === lastWrittenSearch.current) return;
      lastWrittenSearch.current = value;
      updateParams({ search: value }, { resetPage: true });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, updateParams]);

  // Keep the box in sync when the URL changes on its own (back/forward).
  useEffect(() => {
    if (search !== lastWrittenSearch.current) {
      lastWrittenSearch.current = search;
      setSearchInput(search);
    }
  }, [search]);

  const retry = useCallback(() => {
    setToast(null);
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadReports() {
      setLoading(true);
      setError('');

      try {
        const response = await getReportedUsers({
          page,
          limit: PAGE_SIZE,
          status,
          search: search || undefined,
          sort,
        });
        if (cancelled) return;

        const list = Array.isArray(response.data) ? response.data : [];
        const pagination = response.pagination || {};
        setRows(list);
        setTotal(Number(pagination.total) || 0);
        setTotalPages(Number(pagination.totalPages) || 1);
        if (status === 'pending' && !search) emitPendingReportsCount(Number(pagination.total) || 0);
      } catch (err) {
        if (cancelled) return;
        const message = err.message || 'Failed to fetch reports.';
        setError(message);
        setRows([]);
        setTotalPages(1);
        showToast(message, 'error', { label: 'Retry', onClick: retry });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadReports();
    return () => {
      cancelled = true;
    };
  }, [page, status, search, sort, reloadKey, showToast, retry]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [page, status, sort, search]);

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page || loading) return;
    updateParams({ page: nextPage === 1 ? '' : nextPage });
  }

  function openDetails(userId) {
    setDrawerUpdate(null);
    updateParams({ user: userId });
  }

  const closeDetails = useCallback(() => {
    setDrawerUpdate(null);
    updateParams({ user: '' });
  }, [updateParams]);

  const handleNotFound = useCallback((message) => {
    showToast(message, 'error');
    closeDetails();
  }, [showToast, closeDetails]);

  const handleDrawerAction = useCallback((action, user) => {
    setActionTarget({ action, user });
  }, []);

  function handleActionDone(response, target) {
    const userId = target.user.id;
    const updatedUser = response?.data?.user || null;

    showToast(response?.message || 'Reports updated.', 'success');
    emitPendingReportsCount();
    setDrawerUpdate({ userId, user: updatedUser });

    if (status === 'pending') {
      const remaining = rows.filter((row) => row.user?.id !== userId);
      setRows(remaining);
      setTotal((t) => Math.max(0, t - 1));
      // The page emptied out — step back so the admin isn't left on a blank page.
      if (remaining.length === 0 && page > 1) {
        updateParams({ page: page - 1 === 1 ? '' : page - 1 });
      }
      return;
    }

    setRows((prev) => prev.map((row) => (
      row.user?.id === userId
        ? { ...row, user: { ...row.user, ...(updatedUser || {}) }, pendingCount: 0 }
        : row
    )));
  }

  const pageItems = getPageItems(page, totalPages);
  const isPendingTab = status === 'pending';
  const emptyText = search
    ? 'No reported users match your search.'
    : isPendingTab ? 'No pending reports' : 'No reported users';

  return (
    <div className="users-page reports-page main-bg-top">
      <div className="users-header">
        <div className="gift-card-heading">
          <h2>Reports</h2>
          {isPendingTab && !search && !loading && total > 0 && (
            <span className="gift-card-review-count" title="Users waiting for review">
              {pluralize(total, 'user')} to review
            </span>
          )}
        </div>
        <div className="users-filters">
          <div className="users-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search reported user by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={sort}
              onChange={(e) => updateParams({ sort: e.target.value === 'most_reported' ? '' : e.target.value }, { resetPage: true })}
              aria-label="Sort"
            >
              {REPORT_SORTS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="notif-tabs reports-tabs" role="tablist">
        {REPORT_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={status === tab.value}
            className={`notif-tab ${status === tab.value ? 'active' : ''}`}
            onClick={() => {
              if (tab.value !== status) {
                updateParams({ status: tab.value === 'pending' ? '' : tab.value }, { resetPage: true });
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="users-table-scroll" ref={scrollRef}>
        <div className="users-table-wrap">
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Reports</th>
                <th>Last Reported</th>
                <th>Account Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: SKELETON_ROWS }, (_, i) => (
                  <tr key={`skeleton-${i}`} className="report-skeleton-row">
                    <td>
                      <div className="user-cell">
                        <div className="user-cell-avatar gc-skeleton" />
                        <div className="user-cell-info report-skeleton-info">
                          <div className="report-skeleton-line gc-skeleton" style={{ width: 120 }} />
                          <div className="report-skeleton-line gc-skeleton" style={{ width: 170 }} />
                        </div>
                      </div>
                    </td>
                    <td><div className="report-skeleton-line gc-skeleton" style={{ width: 80 }} /></td>
                    <td><div className="report-skeleton-line gc-skeleton" style={{ width: 100 }} /></td>
                    <td><div className="report-skeleton-line gc-skeleton" style={{ width: 60 }} /></td>
                    <td><div className="report-skeleton-line gc-skeleton" style={{ width: 140 }} /></td>
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan="5" className="users-empty">
                    <p>{error}</p>
                    <button type="button" className="btn btn-secondary btn-sm report-retry-btn" onClick={retry}>
                      Retry
                    </button>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="5" className="users-empty">{emptyText}</td>
                </tr>
              ) : (
                rows.map((row) => {
                  const user = row.user || {};
                  const accountStatus = getAccountStatus(user);
                  const reportCount = row.reportCount ?? 0;
                  const pendingCount = row.pendingCount ?? 0;
                  const tooltip = `${reportCount} ${reportCount === 1 ? 'person' : 'people'} reported this user (${pendingCount} pending)`;

                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="user-cell">
                          <ReportUserAvatar user={user} />
                          <div className="user-cell-info">
                            <span className="user-cell-name">{user.name || '—'}</span>
                            <span className="user-cell-email">{user.email || ''}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`report-count-pill ${pendingCount > 0 ? 'has-pending' : ''}`}
                          title={tooltip}
                          aria-label={tooltip}
                          onClick={() => openDetails(user.id)}
                        >
                          {pluralize(reportCount, 'report')}
                        </button>
                      </td>
                      <td>
                        <div className="datetime-cell">
                          <span className="datetime-date">{formatDate(row.lastReportedAt)}</span>
                          <span className="datetime-time">{formatTime(row.lastReportedAt)}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge status-${accountStatus.className}`}>{accountStatus.label}</span>
                      </td>
                      <td>
                        <div className="actions-cell report-actions-cell">
                          <button type="button" className="report-text-btn" onClick={() => openDetails(user.id)}>
                            View reports
                          </button>
                          {isPendingTab && user.isBlocked !== 1 && (
                            <button
                              type="button"
                              className="report-text-btn report-text-btn-danger"
                              onClick={() => setActionTarget({ action: 'block', user })}
                              disabled={Boolean(actionTarget)}
                            >
                              Block
                            </button>
                          )}
                          {isPendingTab && (
                            <button
                              type="button"
                              className="report-text-btn"
                              onClick={() => setActionTarget({ action: 'dismiss', user })}
                              disabled={Boolean(actionTarget)}
                            >
                              Dismiss
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className="users-pagination" aria-label="Pagination">
          <button
            type="button"
            className="pagination-page-btn"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>

          {pageItems.map((item, index) =>
            item === '…' ? (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`pagination-page-btn ${page === item ? 'active' : ''}`}
                onClick={() => goToPage(item)}
                aria-current={page === item ? 'page' : undefined}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            className="pagination-page-btn"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}

      {openUserId && (
        <ReportDetailsDrawer
          key={openUserId}
          userId={openUserId}
          updated={drawerUpdate}
          onClose={closeDetails}
          onNotFound={handleNotFound}
          onAction={handleDrawerAction}
        />
      )}

      <ReportActionModal
        target={actionTarget}
        onClose={() => setActionTarget(null)}
        onDone={handleActionDone}
        onError={(message) => showToast(message, 'error')}
      />

      {toast ? (
        <div className={`gc-toast gc-toast-${toast.kind} report-toast`} role="status">
          <span>{toast.text}</span>
          {toast.action && (
            <button type="button" className="report-toast-action" onClick={toast.action.onClick}>
              {toast.action.label}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
